/**
 * enLandingSections — the lower half of the marketing page: use cases,
 * metrics, integrations, comparison, FAQ, final CTA and footer.
 * Composed into `en` through enLanding.ts.
 */
export const enLandingSections = {
  // ── Use cases by business type ───────────────────────────────────────────
  'landing.useCases.eyebrow': 'Use cases',
  'landing.useCases.title': 'Built for every kind of business',
  'landing.useCases.subtitle': 'Whatever you sell — products, time, knowledge or software — the same platform runs it.',
  'landing.useCases.cta': 'Get the free playbook',
  'landing.useCases.ecommerce': 'E-commerce',
  'landing.useCases.ecommerce.1': 'Abandoned-cart reminders by email and DM',
  'landing.useCases.ecommerce.2': 'Comment-to-DM with a product link',
  'landing.useCases.ecommerce.3': 'Post-purchase review and win-back flows',
  'landing.useCases.services': 'Services & clinics',
  'landing.useCases.services.1': 'Appointments booked from Instagram or WhatsApp',
  'landing.useCases.services.2': 'Reminders that cut no-shows',
  'landing.useCases.services.3': 'Follow-up and rebooking after every visit',
  'landing.useCases.coaching': 'Coaches',
  'landing.useCases.coaching.1': 'Lead magnet delivered by DM, with a follow check',
  'landing.useCases.coaching.2': 'Discovery calls booked with a BuzzCall link',
  'landing.useCases.coaching.3': 'Nurture emails from first call to program',
  'landing.useCases.courses': 'Course creators',
  'landing.useCases.courses.1': 'Waitlist and launch sequences by email',
  'landing.useCases.courses.2': 'Comment-to-DM enrolment links',
  'landing.useCases.courses.3': 'Onboarding and completion nudges',
  'landing.useCases.saas': 'SaaS',
  'landing.useCases.saas.1': 'Trial onboarding emails, day by day',
  'landing.useCases.saas.2': 'Demo calls booked straight from a DM',
  'landing.useCases.saas.3': 'Failed-payment and win-back flows',
  'landing.useCases.creators': 'Creators',
  'landing.useCases.creators.1': 'Comment a keyword, get the link in your DMs',
  'landing.useCases.creators.2': 'Grow your list from story replies',
  'landing.useCases.creators.3': 'Brand-deal enquiries routed to your inbox',

  // ── Grow on autopilot (capability facts, not performance claims) ────────
  'landing.metrics.title': 'Grow on autopilot',
  'landing.metrics.subtitle': 'One platform instead of a stack of tools — and one record for every customer.',
  'landing.metrics.mcp': '100+ tools',
  'landing.metrics.mcpDesc': 'In our MCP connector for your own AI',
  'landing.integrations.title': 'Works with the tools you already use',
  'landing.integrations.instagram': 'Instagram',
  'landing.integrations.facebook': 'Facebook',
  'landing.integrations.whatsapp': 'WhatsApp',
  'landing.integrations.shopify': 'Shopify',
  'landing.integrations.gcal': 'Google Calendar',
  'landing.integrations.payments': 'Stripe & Cardcom',
  'landing.integrations.resend': 'Resend',
  'landing.integrations.mcp': 'Your AI assistant via MCP',

  // ── Comparison ───────────────────────────────────────────────────────────
  'landing.compare.title': 'By hand, a stack of tools, or one platform',
  'landing.compare.subtitle': 'An honest look at what it takes to keep up with every message.',
  'landing.compare.colTask': 'What it takes',
  'landing.compare.colHand': 'By hand',
  'landing.compare.colTools': 'Several tools',
  'landing.compare.colUs': 'BuzzBit X',
  'landing.compare.r2': 'Bookings and reminders',
  'landing.compare.r2.hand': 'Back-and-forth messages',
  'landing.compare.r2.tools': 'Another subscription',
  'landing.compare.r2.us': 'Built in, with BuzzCall video',
  'landing.compare.r3': 'Customer history',
  'landing.compare.r3.hand': 'Spreadsheets and memory',
  'landing.compare.r3.tools': 'Split across tools',
  'landing.compare.r3.us': 'One record per customer',
  'landing.compare.r4': 'Follow-up emails',
  'landing.compare.r4.hand': 'Written one by one',
  'landing.compare.r4.tools': 'A separate email tool',
  'landing.compare.r4.us': 'Flows that send themselves',
  'landing.compare.r5': 'Getting started',
  'landing.compare.r5.hand': 'Nothing to set up — and it never ends',
  'landing.compare.r5.tools': 'Wiring tools together',
  'landing.compare.r5.us': 'Ready-made flows in minutes',

  // ── FAQ ──────────────────────────────────────────────────────────────────
  'landing.faq.title': 'Frequently asked questions',
  'landing.faq.subtitle': 'Everything you need to know before you start.',
  'landing.faq.q1': 'Who is BuzzBit X for?',
  'landing.faq.a1': 'Every kind of business owner — online shops, clinics and salons, consultants and coaches, course creators, SaaS companies and content creators. Pick your business type when you sign up and the app speaks your language: bookings, enrollments, orders or subscriptions.',
  'landing.faq.q6': 'Do my flows use AI credits?',
  'landing.faq.a6': 'No. Flows, campaigns, bookings and reminders run on their own — no AI credits needed. If you want an AI assistant, connect your AI assistant through our MCP connector (100+ tools).',
  'landing.faq.q7': 'Do I need a Shopify store?',
  'landing.faq.a7': 'No. Shopify is one integration among many. Clinics, coaches, course creators, SaaS companies and creators use BuzzBit X with bookings, Stripe or Cardcom payments and their social channels — no store required.',

  // ── Final CTA + footer ───────────────────────────────────────────────────
  'landing.cta.title': 'Your next customer is already in your DMs',
  'landing.cta.primary': 'Get started free',
  'landing.cta.secondary': 'Get the free playbook',
  'landing.footer.rights': '© 2026 BuzzBit X. All rights reserved.',
  'landing.footer.builtIn': 'Built in Israel',
  'landing.footer.navLabel': 'Footer',
  'landing.footer.col.resources': 'Resources',
  'landing.footer.col.legal': 'Legal',
  'landing.footer.col.support': 'Support',
  'landing.footer.gettingStarted': 'Getting started',
  'landing.footer.privacy': 'Privacy Policy',
  'landing.footer.terms': 'Terms of Service',
  'landing.footer.cookies': 'Cookie Policy',
  'landing.footer.acceptableUse': 'Acceptable Use',
  'landing.footer.accessibility': 'Accessibility statement',
  'landing.footer.contact': 'Contact',
} as const;
